import { useEffect, useRef, useState } from "react";
import { Wallet } from "lucide-react";
import api from "../../lib/api";
import Button from "../ui/Button";
import { Card } from "../ui/Card";
import { formatPrice } from "../../lib/utils";

const TOPUP_LABEL = { mtn: "MTN Mobile Money", airtel: "Airtel Money", test: "Test payment" };

const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

/**
 * A SHUWA wallet — the business's (money customers paid online) or, with
 * `customer`, the signed-in user's own (added money and refunds, spent on
 * bookings). Shows the balance, what is on hold until its appointment is
 * completed, and when each amount settled. Only a
 * business withdraws to mobile money; the withdrawal fee is always shown
 * before confirming.
 */
export default function ShuwaWallet({ customer = false }) {
  const endpoint = customer ? "/wallet" : "/owner/wallet";
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupProvider, setTopupProvider] = useState(null);
  const [adding, setAdding] = useState(false);
  const [topupMessage, setTopupMessage] = useState(null);
  const [topupError, setTopupError] = useState(null);

  useEffect(() => {
    api
      .get(endpoint)
      .then(({ data }) => {
        setWallet(data);
        setPhone(data.default_phone || "");
      })
      .catch(() => {});
  }, [endpoint]);

  // A mobile money top-up is confirmed on the customer's phone, so keep
  // checking until it lands (or fails).
  const pendingTopup = Boolean(wallet?.has_pending_topup);
  useEffect(() => {
    if (!pendingTopup) return undefined;
    const timer = setInterval(() => {
      api.get(endpoint).then(({ data }) => setWallet(data)).catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [pendingTopup, endpoint]);

  // Once the wait is over, say how the top-up ended.
  const wasPending = useRef(false);
  const lastTopup = wallet?.last_topup;
  useEffect(() => {
    if (wasPending.current && !pendingTopup) {
      if (lastTopup?.status === "failed") {
        setTopupMessage(null);
        setTopupError(`Your top-up of ${formatPrice(lastTopup.amount)} did not go through. No money was taken.`);
      } else if (lastTopup?.status === "success") {
        setTopupMessage(`${formatPrice(lastTopup.amount)} added to your wallet.`);
      }
    }
    wasPending.current = pendingTopup;
  }, [pendingTopup, lastTopup?.status, lastTopup?.amount]);

  if (!wallet) return null;

  const value = Math.round(Number(amount) || 0);
  const fee = Number(wallet.withdrawal_fee);
  const topupProviders = wallet.topup_providers || [];
  const chosenTopupProvider = topupProviders.includes(topupProvider) ? topupProvider : topupProviders[0];
  const topupValue = Math.round(Number(topupAmount) || 0);

  const addMoney = async () => {
    setAdding(true);
    setTopupError(null);
    setTopupMessage(null);
    try {
      const { data } = await api.post(`${endpoint}/topup`, { amount: topupValue, provider: chosenTopupProvider, phone: phone || undefined });
      setWallet(data.wallet);
      setTopupMessage(data.message);
      setTopupAmount("");
    } catch (e) {
      const data = e.response?.data;
      if (data?.wallet) setWallet(data.wallet);
      setTopupError(data?.errors?.amount?.[0] || data?.message || "The payment could not be started. Please try again.");
    } finally {
      setAdding(false);
    }
  };
  const receives = Math.max(0, value - fee);
  // Money an open refund request may take back stays in the balance but can't be withdrawn.
  const refundHold = Number(wallet.refund_hold || 0);
  const withdrawable = wallet.withdrawable ?? wallet.balance;
  const problem =
    value <= 0
      ? null
      : value < wallet.min_withdrawal
      ? `The minimum withdrawal is ${formatPrice(wallet.min_withdrawal)}.`
      : value > withdrawable
      ? `You can withdraw up to ${formatPrice(withdrawable)}.`
      : !phone.trim()
      ? "Enter the mobile money number to send to."
      : null;

  const withdraw = async () => {
    setSending(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await api.post(`${endpoint}/withdraw`, { amount: value, phone, confirm: true });
      setWallet(data.wallet);
      setMessage(data.message);
      setAmount("");
      setReviewing(false);
    } catch (e) {
      const data = e.response?.data;
      if (data?.wallet) setWallet(data.wallet);
      setError(data?.errors?.amount?.[0] || data?.message || "The withdrawal could not be completed. Please try again.");
      setReviewing(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Wallet className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-slate-900">SHUWA wallet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {customer
              ? "Add money to pay for bookings, and receive refunds here to use on your next booking."
              : "What your customers pay online is held here until each appointment is completed, then released to your balance. Withdraw your balance to mobile money anytime."}
          </p>
        </div>
      </div>

      <div className={`mt-4 grid gap-3 ${customer ? "" : "sm:grid-cols-2"}`}>
        <div className="rounded-xl border border-border p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Balance</p>
          <p className="mt-1 font-montserrat text-xl font-extrabold text-slate-900">{formatPrice(wallet.balance)}</p>
        </div>
        {!customer && (
          <div className="rounded-xl border border-border p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">On hold</p>
            <p className="mt-1 font-montserrat text-xl font-extrabold text-slate-900">{formatPrice(wallet.pending_balance)}</p>
            <p className="mt-1 text-xs text-muted-foreground">Released when each appointment is completed.</p>
          </div>
        )}
      </div>

      {wallet.can_top_up && topupProviders.length > 0 && (
        <div className="mt-5">
          <p className="text-sm font-semibold text-slate-900">Add money</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Adding money is free. Use your balance to pay for bookings at checkout.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <input
              type="number"
              min="0"
              value={topupAmount}
              onChange={(e) => setTopupAmount(e.target.value)}
              placeholder="Amount (RWF)"
              aria-label="Amount to add"
              className="w-full rounded-xl border border-border p-3 text-sm"
            />
            <select
              value={chosenTopupProvider}
              onChange={(e) => setTopupProvider(e.target.value)}
              aria-label="Payment method"
              className="w-full rounded-xl border border-border bg-white p-3 text-sm"
            >
              {topupProviders.map((p) => (
                <option key={p} value={p}>
                  {TOPUP_LABEL[p] || p}
                </option>
              ))}
            </select>
          </div>
          {topupError && <p className="mt-2 text-sm text-red-600">{topupError}</p>}
          {(topupMessage || wallet.has_pending_topup) && (
            <p className="mt-2 text-sm text-emerald-700">
              {wallet.has_pending_topup ? "Approve the payment on your phone — your wallet updates as soon as it is confirmed." : topupMessage}
            </p>
          )}
          <Button className="mt-3" onClick={addMoney} disabled={adding || wallet.has_pending_topup || topupValue < 100}>
            {adding ? "Starting…" : topupValue >= 100 ? `Add ${formatPrice(topupValue)}` : "Add money"}
          </Button>
        </div>
      )}

      {!customer && (
      <div className="mt-5">
        <p className="text-sm font-semibold text-slate-900">Withdraw to mobile money</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Withdrawing money costs {formatPrice(fee)} per withdrawal (SHUWA withdrawal fee).
        </p>
        {refundHold > 0 && (
          <p className="mt-1 text-sm text-amber-700">
            {formatPrice(refundHold)} of your balance is held while a customer's refund request is reviewed. You can
            withdraw up to {formatPrice(withdrawable)} now.
          </p>
        )}
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <input
            type="number"
            min="0"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setReviewing(false);
            }}
            placeholder="Amount (RWF)"
            aria-label="Amount to withdraw"
            className="w-full rounded-xl border border-border p-3 text-sm"
          />
          <input
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setReviewing(false);
            }}
            placeholder="Mobile money number"
            aria-label="Mobile money number"
            className="w-full rounded-xl border border-border p-3 text-sm"
          />
        </div>

        {value > 0 && (
          <div className="mt-3 space-y-1 rounded-xl border border-border bg-muted/30 p-3 text-sm">
            <div className="flex justify-between"><span>Leaves your wallet</span><span>{formatPrice(value)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>SHUWA withdrawal fee</span><span>− {formatPrice(fee)}</span></div>
            <div className="flex justify-between font-semibold"><span>You receive</span><span>{formatPrice(receives)}</span></div>
          </div>
        )}
        {problem && <p className="mt-2 text-sm text-red-600">{problem}</p>}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        {message && <p className="mt-2 text-sm text-emerald-700">{message}</p>}

        {reviewing ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={withdraw} disabled={sending}>
              {sending ? "Sending…" : `Confirm — send ${formatPrice(receives)} to ${phone}`}
            </Button>
            <Button variant="outline" onClick={() => setReviewing(false)} disabled={sending}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button className="mt-3" onClick={() => setReviewing(true)} disabled={value <= 0 || !!problem}>
            Review withdrawal
          </Button>
        )}
      </div>
      )}

      {wallet.transactions.length > 0 && (
        <div className="mt-5">
          <p className="text-sm font-semibold text-slate-900">Recent activity</p>
          <ul className="mt-2 divide-y divide-border text-sm">
            {wallet.transactions.map((tx) => (
              <li key={tx.id} className="flex items-center justify-between gap-3 py-2">
                <div>
                  <p className="text-slate-800">{tx.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {tx.status === "pending"
                      ? "On hold until the appointment is completed"
                      : `${Number(tx.amount) > 0 ? "Settled " : ""}${formatDateTime(tx.available_at || tx.created_at)}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={Number(tx.amount) < 0 ? "text-slate-700" : "font-semibold text-emerald-700"}>
                    {Number(tx.amount) < 0 ? "− " : "+ "}
                    {formatPrice(Math.abs(Number(tx.amount)))}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
